import {
  OpeningHoursItem as FormattedOpeningHoursProps,
  StoreService as AvailableService,
  PaymentMethodItem,
  type LinkProps
} from '@dcxwam/dcx-wam-ui-foundations'
import type {
  GeoLocation,
  OpeningHour,
  Store,
  Address
} from '../../../services/stores/stores-sdk'
import {
  CategoryProps,
  OpenStatus,
  DaysProps,
  StoreServiceConfig,
  PaymentMethodConfig,
  TransformedStoreDetailsData,
  TransformStoreDetailsInput,
  DataLayerItem
} from './types'
import {
  REPLACE_TIME,
  REPLACE_WEEK_DAY,
  REPLACE_CITY,
  REPLACE_STORE_ID,
  REPLACE_ADDRESS_LINE1,
  STOREWITHWEB
} from '../../../utils/constants'
import { canUseDom } from '../../../utils/isSsr'
import { normalizeString } from '../../../utils/string'

/**
 * Replaces all spaces in a string with dashes.
 * Example: "New York" -> "New-York"
 */
export const normalizeSpacesToDashes = (text: string): string => {
  return text?.replace(/\s+/g, '-')
}
/**
 * Cleans and normalizes a string for use in URLs or slugs.
 *
 * Steps performed:
 * 1. Converts the string to lowercase.
 * 2. Removes all accents and diacritics using the `remove-accents` library (e.g., "ö" -> "o", "ß" -> "ss").
 * 3. Converts spaces to dashes using `normalizeSpacesToDashes`.
 * 4. Replaces slashes (/) with dashes, removes dots (.) and hash symbols (#).
 * 5. Collapses multiple consecutive dashes into one.
 * 6. Removes any leading or trailing dashes.
 *
 * Example usage:
 * accentsTidy("São Paulo #1") => "sao-paulo-1"
 * accentsTidy("Straße 12/34") => "strasse-12-34"
 *
 * @param s - The input string to clean and normalize
 * @returns A lowercase, accent-free, URL-friendly string
 */
export const accentsTidy = (s: string): string => {
  if (!s || typeof s !== 'string') {
    return ''
  }
  const normalized = normalizeString(s).toLowerCase()

  return normalizeSpacesToDashes(normalized)
    .replace(/\//g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/(-)+/g, '-')
    .replace(/(^-)|(-$)/g, '')
}

export const createStoreDetailUrl = (
  store: Store,
  storeDetailPageUrl: string
): string => {
  const { address, storeId } = store
  const city = address?.city?.trim()
  const addressLine1 = address?.addressLine1?.trim()

  if (
    !city ||
    !addressLine1 ||
    !storeId ||
    !storeDetailPageUrl ||
    store?.storeInfoUse !== STOREWITHWEB
  ) {
    return ''
  }

  return storeDetailPageUrl
    .replace(REPLACE_CITY, accentsTidy(city))
    .replace(REPLACE_ADDRESS_LINE1, accentsTidy(addressLine1))
    .replace(REPLACE_STORE_ID, storeId)
}

export const formatTime = (
  isoTime: string | null,
  enable12HourFormat = false,
  locale: string = canUseDom ? navigator.language : 'en-US'
): string => {
  if (!isoTime) {
    return ''
  }

  const time24 = isoTime.slice(0, 5)

  if (!enable12HourFormat) {
    return time24
  }

  const [hour, minute] = time24.split(':').map(Number)

  const date = new Date()
  date.setHours(hour, minute, 0, 0)

  return date.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

const handleValidStore = (
  dateEntries: {
    today: Date
    todayEntry: OpeningHour
    nextDayEntry: OpeningHour | undefined
  },
  config: {
    labels: {
      openedUntilLabel: string
      willOpenAtLabel: string
      openingHoursNotAvailable?: string
    }
    days: DaysProps
    enable12HourFormat?: boolean
  }
) => {
  const { openedUntilLabel, willOpenAtLabel } = config.labels
  const { todayEntry, nextDayEntry, today } = dateEntries

  const year = today.getFullYear()
  const month = today.getMonth()
  const date = today.getDate()

  const openingTimeArray = formatTime(todayEntry?.openingTime)?.split(':') || []
  const openingTimeHour = openingTimeArray[0] || ''
  const openingTimeMin = openingTimeArray[1] || ''
  const openingTime = new Date(
    year,
    month,
    date,
    Number(openingTimeHour),
    Number(openingTimeMin)
  )
  const closingTimeArray = formatTime(todayEntry?.closingTime)?.split(':') || []
  const closingTimeHour = closingTimeArray[0] || ''
  const closingTimeMin = closingTimeArray[1] || ''
  const currentTime = new Date(
    year,
    month,
    date,
    today.getHours(),
    today.getMinutes()
  )
  const closingTime = new Date(
    year,
    month,
    date,
    Number(closingTimeHour),
    Number(closingTimeMin)
  )

  if (currentTime >= openingTime && currentTime < closingTime) {
    return {
      status: OpenStatus.Open,
      message: openedUntilLabel?.replace(
        REPLACE_TIME,
        formatTime(todayEntry?.closingTime, config.enable12HourFormat)
      )
    }
  }

  if (currentTime < openingTime) {
    const dayLabel = config.days[todayEntry?.dayOfWeek.name.toLowerCase()]
    return {
      status: OpenStatus.Closed,
      message: willOpenAtLabel
        ?.replace(
          REPLACE_TIME,
          formatTime(todayEntry?.openingTime, config.enable12HourFormat)
        )
        ?.replace(REPLACE_WEEK_DAY, dayLabel)
    }
  }

  if (currentTime >= closingTime) {
    const dayLabel =
      config.days[nextDayEntry?.dayOfWeek.name.toLowerCase() ?? '']
    return {
      status: OpenStatus.Closed,
      message: willOpenAtLabel
        ?.replace(
          REPLACE_TIME,
          formatTime(nextDayEntry?.openingTime ?? '', config.enable12HourFormat)
        )
        ?.replace(REPLACE_WEEK_DAY, dayLabel)
    }
  }

  return {
    status: OpenStatus.Closed,
    message: ''
  }
}

export const getCategoryDetails = (
  categories: Array<CategoryProps>,
  type: string
) => categories?.find((item) => item.id === type)

export const getCurrentDayOpenCloseMessage = (
  openingHours: Array<OpeningHour>,
  config: {
    labels: {
      openedUntilLabel: string
      willOpenAtLabel: string
      openingHoursNotAvailable?: string
    }
    days: DaysProps
    enable12HourFormat?: boolean
  }
) => {
  const { willOpenAtLabel, openingHoursNotAvailable } = config.labels
  if (!openingHours?.length) {
    return {
      status: OpenStatus.NotAvailable,
      message: openingHoursNotAvailable ?? ''
    }
  }
  const hasAnyValidOpeningHours = openingHours.some(
    (item) => item.openingTime && item.closingTime
  )
  if (!hasAnyValidOpeningHours) {
    return {
      status: OpenStatus.NotAvailable,
      message: openingHoursNotAvailable ?? ''
    }
  }
  if (openingHours.filter((item) => item.openingTime !== null).length === 0) {
    return {
      status: OpenStatus.Closed,
      message: ''
    }
  }
  const today = new Date()
  const day = today.getDay()
  const todayEntry = openingHours?.find((item) => item.dayOfWeek.index === day)
  let nextDayEntry = openingHours?.find(
    (item) =>
      item.dayOfWeek.index > day &&
      item.dayOfWeek.index <= 6 &&
      item.openingTime !== null
  )
  if (!nextDayEntry) {
    nextDayEntry = openingHours?.find(
      (item) =>
        item.dayOfWeek.index < day &&
        item.dayOfWeek.index >= 0 &&
        item.openingTime !== null
    )
  }
  if (!todayEntry?.closingTime && !todayEntry?.openingTime) {
    const dayLabel =
      config.days[nextDayEntry?.dayOfWeek.name.toLowerCase() ?? '']
    return {
      status: OpenStatus.Closed,
      message: willOpenAtLabel
        ?.replace(
          REPLACE_TIME,
          formatTime(nextDayEntry?.openingTime ?? '', config.enable12HourFormat)
        )
        ?.replace(REPLACE_WEEK_DAY, dayLabel)
    }
  }
  if (todayEntry?.openingTime) {
    return handleValidStore({ today, todayEntry, nextDayEntry }, config)
  }

  return {
    status: OpenStatus.Closed,
    message: ''
  }
}

interface DayInfoProps {
  name: string
  index: number
  normalizedIndex: number
}

interface TimeGroupProps {
  openTime: string | null
  closeTime: string | null
  days: DayInfoProps[]
}

const CLOSED_KEY = 'closed'
const SUNDAY_NORMALIZED_INDEX = 7

const normalizeIndex = (index: number): number => {
  return index || SUNDAY_NORMALIZED_INDEX
}

const findConsecutiveDayRanges = (days: DayInfoProps[]): DayInfoProps[][] => {
  if (days.length <= 1) {
    return [days]
  }

  const consecutiveDayRanges: DayInfoProps[][] = []
  let currentRange = [days[0]]

  const remainingDays = days.slice(1)
  remainingDays.forEach((currentDay) => {
    const previousDay = currentRange[currentRange.length - 1]

    if (currentDay.normalizedIndex === previousDay.normalizedIndex + 1) {
      currentRange.push(currentDay)
    } else {
      consecutiveDayRanges.push(currentRange)
      currentRange = [currentDay]
    }
  })

  consecutiveDayRanges.push(currentRange)
  return consecutiveDayRanges
}

const formatCondensedView = (
  openingHourGroups: Map<string, TimeGroupProps>,
  dayNameMap: DaysProps,
  closedLabel: string
): FormattedOpeningHoursProps[] => {
  const result: Array<FormattedOpeningHoursProps & { sortOrder: number }> = []

  Array.from(openingHourGroups).forEach(([key, timeGroup]) => {
    const timeRange =
      key === CLOSED_KEY
        ? closedLabel
        : `${timeGroup.openTime} - ${timeGroup.closeTime}`
    const consecutiveDayRanges = findConsecutiveDayRanges(timeGroup.days)

    consecutiveDayRanges
      .filter((range) => range.length > 0)
      .forEach((range) => {
        const firstDayName = dayNameMap[range[0].name.toLowerCase()]
        const lastDayName =
          dayNameMap[range[range.length - 1].name.toLowerCase()]
        const dayRange =
          range.length === 1 ? firstDayName : `${firstDayName} - ${lastDayName}`

        result.push({
          days: dayRange,
          hours: timeRange,
          dayIndex: undefined,
          sortOrder: range[0].normalizedIndex
        })
      })
  })

  result.sort((a, b) => a.sortOrder - b.sortOrder)
  return result.map(({ sortOrder, ...item }) => item)
}

const formatExpandedView = (
  openingHourGroups: Map<string, TimeGroupProps>,
  dayNameMap: DaysProps,
  closedLabel: string
): FormattedOpeningHoursProps[] => {
  const allDays: Array<FormattedOpeningHoursProps & { sortOrder: number }> = []

  Array.from(openingHourGroups).forEach(([key, timeGroup]) => {
    const timeRange =
      key === CLOSED_KEY
        ? closedLabel
        : `${timeGroup.openTime} - ${timeGroup.closeTime}`

    timeGroup.days.forEach((day) => {
      allDays.push({
        days: dayNameMap[day.name.toLowerCase()],
        hours: timeRange,
        dayIndex: day.index,
        sortOrder: day.normalizedIndex
      })
    })
  })

  allDays.sort((a, b) => a.sortOrder - b.sortOrder)
  return allDays.map(({ sortOrder, ...item }) => item)
}

export const getOpeningHours = (
  openingHours: OpeningHour[],
  config: {
    days: DaysProps
    closedLabel: string
    enable12HourFormat?: boolean
    enableExpandedOpeningHoursView?: boolean
  }
): FormattedOpeningHoursProps[] => {
  const openingHourGroups = new Map<string, TimeGroupProps>()
  let openGroupCount = 0

  openingHours.forEach((day) => {
    const openTime = formatTime(day.openingTime, config.enable12HourFormat)
    const closeTime = formatTime(day.closingTime, config.enable12HourFormat)
    const isClosed = day.isClosed || !openTime || !closeTime
    const key = isClosed ? CLOSED_KEY : `${openTime}-${closeTime}`

    if (!openingHourGroups.has(key)) {
      openingHourGroups.set(key, { openTime, closeTime, days: [] })
      if (key !== CLOSED_KEY) {
        openGroupCount += 1
      }
    }

    const { dayOfWeek } = day
    const normalizedIndex = normalizeIndex(dayOfWeek.index)

    const timeGroup = openingHourGroups.get(key)
    if (timeGroup) {
      timeGroup.days.push({
        name: dayOfWeek.name.toLowerCase(),
        index: dayOfWeek.index,
        normalizedIndex
      })
    }
  })

  Array.from(openingHourGroups.values()).forEach((timeGroup) => {
    timeGroup.days.sort((a, b) => a.normalizedIndex - b.normalizedIndex)
  })

  // If enableExpandedOpeningHoursView is enabled, always use expanded view
  if (config.enableExpandedOpeningHoursView) {
    return formatExpandedView(
      openingHourGroups,
      config.days,
      config.closedLabel
    )
  }

  const shouldUseCondensed = openGroupCount <= 1
  if (shouldUseCondensed) {
    return formatCondensedView(
      openingHourGroups,
      config.days,
      config.closedLabel
    )
  }
  return formatExpandedView(openingHourGroups, config.days, config.closedLabel)
}

export const getStoreServices = (
  servicesConfig: StoreServiceConfig[],
  storeServices: string[]
): AvailableService[] => {
  if (!Array.isArray(servicesConfig) || !Array.isArray(storeServices)) {
    return []
  }
  return servicesConfig.reduce(
    (availableServices: AvailableService[], configItem: StoreServiceConfig) => {
      const { icon = '', label = '', types = '', description = '' } = configItem

      if (!types.trim() || !label.trim()) {
        return availableServices
      }

      const serviceIds: string[] = types
        .split('|')
        .map((serviceType: string) => serviceType.trim())
        .filter(Boolean)

      const hasMatch = serviceIds.some((serviceId) =>
        storeServices.includes(serviceId)
      )
      if (!hasMatch) {
        return availableServices
      }

      availableServices.push({
        icon,
        label,
        description
      })

      return availableServices
    },
    []
  )
}

export const getPaymentMethods = (
  paymentsConfig: PaymentMethodConfig[],
  storePayments: string[]
): PaymentMethodItem[] => {
  const storePaymentsSet = new Set(storePayments)
  return paymentsConfig.filter((paymentConfig) =>
    storePaymentsSet.has(paymentConfig.key)
  )
}

export const getCityLinkUrl = (
  storeDirectoryPageUrl: string | undefined,
  city: string,
  storeDirectoryCities?: string[]
): string | undefined => {
  if (!storeDirectoryPageUrl) {
    return undefined
  }
  const normalizedCity = accentsTidy(city)

  if (!storeDirectoryCities || storeDirectoryCities.length === 0) {
    return storeDirectoryPageUrl.replace(REPLACE_CITY, normalizedCity)
  }
  const allowedCities = new Set(storeDirectoryCities.map((v) => accentsTidy(v)))

  return allowedCities.has(normalizedCity)
    ? storeDirectoryPageUrl.replace(REPLACE_CITY, normalizedCity)
    : undefined
}

export const getGoToMapLink = (
  goToMapViewLabel: string | undefined,
  storeMapPageUrl: string | undefined,
  storeLocation: { latitude?: number; longitude?: number } | undefined
): LinkProps | undefined => {
  if (
    !goToMapViewLabel ||
    !storeMapPageUrl ||
    !storeLocation?.latitude ||
    !storeLocation?.longitude
  ) {
    return undefined
  }
  const isFromStoreMapPage =
    canUseDom && document.referrer.includes(storeMapPageUrl)

  let linkUrl: string
  if (isFromStoreMapPage) {
    linkUrl = document.referrer
  } else {
    const separator = storeMapPageUrl.includes('?') ? '&' : '?'
    linkUrl = `${storeMapPageUrl}${separator}lat=${storeLocation.latitude}&lng=${storeLocation.longitude}`
  }

  return {
    icon: 'pmi-icon-location',
    label: goToMapViewLabel,
    linkUrl
  }
}

export const transformStoreDetailsData = ({
  storeDetailsConfig,
  storeLocatorConfig,
  storeData
}: TransformStoreDetailsInput): TransformedStoreDetailsData => {
  const storeAddress = storeData?.address
  const storeLocation = storeAddress?.location
  const storeServices = storeData?.services
  const {
    mapZoomLevel,
    paymentMethodsTitle,
    payments,
    mapButtonLabel,
    breadcrumbCountryLabel,
    goToMapViewLabel
  } = storeDetailsConfig
  const {
    mapUserIcon,
    categories,
    days,
    closedLabel,
    openingHoursNotAvailable,
    openingHoursTitle,
    showBookAnAppointmentServiceEntry,
    bookAnAppointmentLabel,
    bookAnAppointmentLink,
    loginPageLink,
    googleMapsAPIKey,
    showDirectionsLabel,
    distanceLabel,
    servicesListTitle,
    openedUntilLabel,
    willOpenAtLabel,
    enable12HourFormat,
    enableMilesAsDistanceUnit,
    enableExpandedOpeningHoursView,
    services,
    storeDirectoryPageUrl,
    storeDirectoryCities,
    storeMapPageUrl,
    storeAddressFormatTemplate
  } = storeLocatorConfig

  return {
    labels: {
      // store details
      paymentMethodsTitle,
      mapButtonLabel,
      breadcrumbCountryLabel,
      goToMapViewLabel,

      // store locator
      openedUntilLabel,
      willOpenAtLabel,
      closedLabel,
      openingHoursNotAvailable,
      openingHoursTitle,
      bookAnAppointmentLabel,
      showDirectionsLabel,
      distanceLabel,
      servicesListTitle
    },
    configs: {
      // store detail configs
      categories,
      mapZoomLevel,
      mapUserIcon,
      paymentsConfig: payments,
      // store locator configs
      days,
      loginPageLink,
      googleMapsAPIKey,
      bookAnAppointmentLink,
      enable12HourFormat,
      enableMilesAsDistanceUnit,
      enableExpandedOpeningHoursView,
      showBookAnAppointmentServiceEntry,
      servicesConfig: services,
      storeDirectoryPageUrl,
      storeDirectoryCities,
      storeMapPageUrl,
      storeAddressFormatTemplate,

      // store data
      storeAddress,
      storeLocation,

      shouldShowBookAnAppointment: !!(
        showBookAnAppointmentServiceEntry &&
        bookAnAppointmentLabel &&
        bookAnAppointmentLink &&
        loginPageLink &&
        storeServices?.includes('APPOINTMENTS')
      ),

      shouldShowMapService: !!(
        storeLocation?.latitude &&
        storeLocation?.longitude &&
        storeData?.name
      )
    }
  }
}

export const getStoreDistanceInMeters = (
  storeLoc?: GeoLocation,
  userLocation?: GeoLocation,
  isSsr = false
) => {
  if (
    isSsr ||
    !window?.google?.maps?.geometry?.spherical ||
    !storeLoc?.latitude ||
    !storeLoc?.longitude ||
    !userLocation?.latitude ||
    !userLocation?.longitude
  ) {
    return undefined
  }

  const location1 = new window.google.maps.LatLng(
    userLocation?.latitude,
    userLocation?.longitude
  )
  const location2 = new window.google.maps.LatLng(
    storeLoc?.latitude,
    storeLoc?.longitude
  )

  // provides distance in meters
  const distanceInMeters =
    window.google.maps.geometry.spherical.computeDistanceBetween(
      location1,
      location2
    )

  const roundedDistanceinMeters = Math.round(distanceInMeters)

  return roundedDistanceinMeters
}

export const buildDataLayerStoreObj = (
  eventName: string,
  storeData:
    | {
        storeId?: string
        name?: string
        type?: string
      }
    | null
    | undefined
): DataLayerItem[] => {
  if (!storeData) {
    return []
  }
  return [
    { key: 'event', value: eventName },
    { key: 'StoreLocatorStoreID', value: storeData.storeId ?? '' },
    { key: 'StoreLocatorStoreName', value: storeData.name ?? '' },
    { key: 'StoreLocatorStoreType', value: storeData.type ?? '' }
  ]
}

export const convertMetersToMilesOrFeet = (meters: number): string => {
  const METERS_IN_MILE = 1609.344
  const METERS_IN_FOOT = 0.3048

  const miles = meters / METERS_IN_MILE

  if (miles < 0.1) {
    const feet = meters / METERS_IN_FOOT
    return `${feet.toFixed(0)}ft`
  }
  return `${miles.toFixed(2)}mi`
}

export const calculateStoreDistance = (
  storeLoc?: GeoLocation,
  userLocation?: GeoLocation,
  options?: {
    enableMilesAsDistanceUnit?: boolean
    isSsr?: boolean
  }
) => {
  const { enableMilesAsDistanceUnit = false, isSsr = false } = options || {}
  const roundedDistanceinMeters = getStoreDistanceInMeters(
    storeLoc,
    userLocation,
    isSsr
  )

  if (!roundedDistanceinMeters) {
    return undefined
  }

  if (enableMilesAsDistanceUnit) {
    return convertMetersToMilesOrFeet(roundedDistanceinMeters)
  }
  // Default metric system behavior
  return roundedDistanceinMeters > 999
    ? `${Math.round(roundedDistanceinMeters / 1000)}km`
    : `${roundedDistanceinMeters}m`
}

export const getFormattedAddress = (address: Address, template?: string) => {
  if (!template) {
    return address?.formattedAddress
  }

  return template.replace(/\$(\w+)/g, (_match: string, key: string) => {
    const addressKey = key as keyof typeof address

    return address && addressKey in address && address[addressKey]
      ? String(address[addressKey])
      : ''
  })
}
